import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { F, Button, Flex } from 'nallian-shared-ui/lib/components';
import Feature from './Feature';

const FieldFlex = styled(Flex)`
  && .field {
    width: 100%;
  }
`;

const AppFeatureFields = ({
  appInstallationId,
  apps,
  fields,
  submitProcessing,
  readOnly,
  editMode,
}) => {
  const { t } = useTranslation();

  const handleAddFieldClick = useCallback(() => fields.push({ isNew: true }), [fields]);
  const handleRemoveFieldClick = useCallback((_, { i }) => fields.remove(i), [fields]);

  const features = useMemo(() => {
    const app = (apps || []).find(a => a.appInstallation.id === appInstallationId);
    if (app?.communityAppFeatures?.length) {
      return app.communityAppFeatures.map(caf => ({
        ...caf,
        description: undefined, // because otherwise it's shown in dropdown
        key: caf.appInstallationFeature.tag,
        value: caf.appInstallationFeature.tag,
        text: caf.alias || caf.appInstallationFeature.name,
      }));
    }
    return [];
  }, [apps, appInstallationId]);

  useEffect(() => {
    if (fields.length <= 0) fields.push({ isNew: true });
  }, [fields]);

  return (
    <F.Field error={false}>
      <Flex fluid margin="-1.5rem 0 0" direction="column">
        <FieldFlex fluid direction="column" alignItems="flex-start">
          {fields.map((f, index) => (
            <Feature
              features={features}
              index={index}
              fields={fields}
              editMode={editMode}
              field={f}
              readOnly={readOnly}
              key={`feature_${index.toString()}`}
              handleRemoveFieldClick={handleRemoveFieldClick}
              submitProcessing={submitProcessing}
            />
          ))}
          {!readOnly && fields.length < features?.length && (
            <Flex fluid justifyContent="flex-end" margin="1rem 0 0 0">
              <Button
                linkbutton
                icon="add"
                type="button"
                tabindex="-1"
                content={t('addFeature')}
                onClick={handleAddFieldClick}
              />
            </Flex>
          )}
        </FieldFlex>
      </Flex>
    </F.Field>
  );
};

AppFeatureFields.propTypes = {
  fields: PropTypes.array.isRequired,
  submitProcessing: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  appInstallationId: PropTypes.string.isRequired,
  apps: PropTypes.array.isRequired,
  editMode: PropTypes.bool.isRequired,
};

export default AppFeatureFields;
